import React, { useState, useEffect, useContext } from "react";
import {
	Box,
	Tabs,
	Tab,
	Typography,
	CircularProgress,
	List,
	ListItem,
	ListItemText,
	IconButton,
	Tooltip,
	Modal,
	Card,
	CardContent,
	Grid,
} from "@mui/material";
import { GlobalContext } from "../../GlobalContext";
import { useTranslation } from "react-i18next";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BarChart from "@mui/icons-material/BarChart";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { obtenerDatos, actualizarDatoReview } from "../../api/config";
import Swal from "sweetalert2";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { SimilarityTable } from "./SimilarityTable";
function TabPanel({ children, value, index }) {
	return (
		<div role="tabpanel" hidden={value !== index}>
			{value === index && <Box sx={{ p: 2 }}>{children}</Box>}
		</div>
	);
}

const combineContractsData = (
	contractsPerDay = [], // Valor por defecto = array vacío
	contractsPerPeriods = [], // Valor por defecto = array vacío
	t,
) => {
	// Si no fuera un array, convertimos a []
	if (!Array.isArray(contractsPerDay)) contractsPerDay = [];
	if (!Array.isArray(contractsPerPeriods)) contractsPerPeriods = [];

	const combinedContracts = [];
	const dayNames = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

	contractsPerDay.forEach((dayContract) => {
		const { config_contracts_per_days_id, day, value, ...rest } = dayContract;

		// Si el value es 0, añadimos directamente "No disponible"
		if (value === 0) {
			combinedContracts.push({
				day: dayNames[day], // Cambiar el número del día por el nombre
				...rest,
				// eslint-disable-next-line no-undef
				periods: [{ start_time: t("aux.no_available"), end_time: "" }],
			});
			return; // Siguiente elemento
		}

		// Buscamos el periodo correspondiente
		const correspondingPeriod = contractsPerPeriods.find(
			(period) => period.config_contracts_per_days_id === config_contracts_per_days_id,
		);

		// Ver si ya existe un día en combinedContracts
		const existingDay = combinedContracts.find((contract) => contract.day === dayNames[day]);

		if (existingDay) {
			existingDay.periods = [
				...existingDay.periods,
				...(correspondingPeriod ? correspondingPeriod.periods : []),
			];
		} else {
			combinedContracts.push({
				day: dayNames[day],
				...rest,
				periods: correspondingPeriod ? correspondingPeriod.periods : [],
			});
		}
	});

	// Ajustar periodos "No disponible" si corresponde
	combinedContracts.forEach((contract) => {
		contract.periods = (contract.periods || []).map(({ start_time, end_time }) => {
			if ((start_time === "00:00:00" && end_time === "00:00:00") || start_time === end_time) {
				// eslint-disable-next-line no-undef
				return { start_time: t("aux.no_available"), end_time: "" };
			}
			return { start_time, end_time };
		});
	});

	return combinedContracts;
};

const getMesesNombres = (t) => [
	{ id: 1, nombre: t("aux.months.Jan") },
	{ id: 2, nombre: t("aux.months.Feb") },
	{ id: 3, nombre: t("aux.months.Mar") },
	{ id: 4, nombre: t("aux.months.Apr") },
	{ id: 5, nombre: t("aux.months.May") },
	{ id: 6, nombre: t("aux.months.Jun") },
	{ id: 7, nombre: t("aux.months.Jul") },
	{ id: 8, nombre: t("aux.months.Aug") },
	{ id: 9, nombre: t("aux.months.Sep") },
	{ id: 10, nombre: t("aux.months.Oct") },
	{ id: 11, nombre: t("aux.months.Nov") },
	{ id: 12, nombre: t("aux.months.Dec") },
];

const ModuleReview = ({ setRightTitle, setRightComponent }) => {
	const { t } = useTranslation();
	const { site_id, handlerSideRight } = useContext(GlobalContext);

	const [openModal, setOpenModal] = useState(false);
	const [openModalChart, setOpenModalChart] = useState(false);
	const [selectedKey, setSelectedKey] = useState(null);
	const [selectedItemName, setSelectedItemName] = useState("");
	const [selectedItemDescription, setSelectedItemDescription] = useState("");
	const [selectedItemTienda, setSelectedItemTienda] = useState({});
	const [selectedItemContrato, setSelectedItemContrato] = useState([]);
	const [tabValue, setTabValue] = useState(0);

	const [dataRevision, setDataRevision] = useState([]);
	const [dataAprobadas, setDataAprobadas] = useState([]);
	const [dataRechazadas, setDataRechazadas] = useState([]);

	// Estado para loading (mostrar spinner)
	const [loading, setLoading] = useState(false);

	const handleOpenSideRight = (simulation) => {
		setRightTitle(simulation?.name || t("aux.unnamed"));
		const similarity = simulation?.similitudes;
		setRightComponent(
			<Box>
				<SimilarityTable similarity={similarity} />
			</Box>,
		);
		handlerSideRight(true);
	};

	const handleVisibility = (key, dataList) => {
		const simulation = dataList[key];
		if (!simulation) return;

		setSelectedItemName(simulation.name || "");
		setSelectedItemDescription(simulation.description || "");
		setSelectedItemTienda(simulation.config_tienda || {});
		setSelectedItemContrato(simulation.config || []);
		setOpenModal(true);
	};

	/**
	 * Abre el modal de gráficas (ModalChart)
	 */
	const handleOpenChart = (key) => {
		setSelectedKey(key);
		setOpenModalChart(true);
	};

	/**
	 * Cierra ambos modales
	 */
	const handleCloseModals = () => {
		setOpenModal(false);
		setOpenModalChart(false);
	};

	/**
	 * Manejador de cambio de pestaña
	 */
	const handleChangeTab = (event, newValue) => {
		setTabValue(newValue);
	};

	/**
	 * Función que llama al endpoint para obtener datos por status.
	 */
	const fetchDataByStatus = async (statusId) => {
		if (!site_id) return [];
		try {
			const response = await obtenerDatos(`/simulacion/by_site_id/${site_id}?status_id=${statusId}`);
			console.log(`Datos obtenidos para status_id=${statusId}:`, response);
			return response;
		} catch (error) {
			console.error("Error fetchDataByStatus:", error);
			return [];
		}
	};

	useEffect(() => {
		if (!site_id) return;

		setLoading(true); // Iniciamos spinner

		Promise.all([
			fetchDataByStatus(1), // En Revisión
			fetchDataByStatus(2), // Aprobadas
			fetchDataByStatus(3), // Rechazadas
		])
			.then(([rev, apr, rej]) => {
				setDataRevision(rev);
				setDataAprobadas(apr);
				setDataRechazadas(rej);
			})
			.catch((error) => {
				console.error("Error cargando datos:", error);
			})
			.finally(() => {
				setLoading(false); // Quitamos spinner
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [site_id]);

	/**
	 * Actualiza el estado (aprobar o rechazar)
	 */
	const handleUpdateStatus = async (task_id, newStatusId) => {
		try {
			setLoading(true);
			Swal.fire({
				title: t("aux.messages.alert.updating"),
				text: t("aux.messages.alert.text_alert.updating_text"),
				icon: "info",
				showConfirmButton: false,
				allowOutsideClick: false,
			});
			Swal.showLoading();
			await actualizarDatoReview(
				`/task/update_status/?task_id=${task_id}&status_id=${newStatusId}&site_id=${site_id}`,
			);

			Swal.close();
			Swal.fire({
				title: t("aux.messages.alert.success"),
				text: t("aux.messages.alert.text_alert.update_success"),
				icon: "success",
			});

			// Refrescar la data de las 3 pestañas tras aprobar/rechazar
			const [rev, apr, rej] = await Promise.all([
				fetchDataByStatus(1),
				fetchDataByStatus(2),
				fetchDataByStatus(3),
			]);
			setDataRevision(rev);
			setDataAprobadas(apr);
			setDataRechazadas(rej);
		} catch (error) {
			Swal.close();
			console.error("Error al actualizar estado:", error);
			Swal.fire({
				title: t("aux.messages.alert.error"),
				text: t("aux.messages.alert.text_alert.update_error"),
				icon: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleApprove = (simulation) => {
		const { task_id, possible_months = [] } = simulation;

		if (possible_months.includes("all")) {
			handleUpdateStatus(task_id, 2);
		} else {
			const mesesNormalizados = possible_months
				.filter(Boolean) // por si entra algún null/undefined
				.map((m) => {
					// Caso nuevo: "YYYY-MM"
					if (m.includes("-")) {
						const [yearStr, monthStr] = m.split("-");
						return {
							key: m,
							year: parseInt(yearStr, 10),
							month: parseInt(monthStr, 10),
						};
					}
					// Caso antiguo: "MM"
					return {
						key: m,
						year: null, // sin año → null
						month: parseInt(m, 10),
					};
				})
				// 3️⃣ Orden cronológico (primero año, luego mes)
				.sort((a, b) => {
					// null year significa «sin año», lo consideramos menor que cualquier año real
					if (a.year === b.year) return a.month - b.month;
					if (a.year === null) return -1;
					if (b.year === null) return 1;
					return a.year - b.year;
				});

			const mesesNombres = mesesNormalizados
				.map(({ year, month }) => {
					const nombreMes = getMesesNombres(t).find((m) => m.id === month)?.nombre ?? month;
					return year ? `${nombreMes} ${year}` : nombreMes; // si no hay año, solo mes
				})
				.join(", ");

			Swal.fire({
				title: "Confirmar aprobación parcial",
				text: `Esta aprobación será válida solo para los siguientes meses: ${mesesNombres}. ¿Deseas continuar?`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#3085d6",
				cancelButtonColor: "#d33",
				confirmButtonText: "Sí, aprobar",
				cancelButtonText: "Cancelar",
			}).then((result) => {
				if (result.isConfirmed) {
					handleUpdateStatus(task_id, 2);
				}
			});
		}
	};

	/**
	 * Renderiza la lista (List) con los datos correspondientes (dataList).
	 */
	const renderList = (dataList, statusId) => {
		const keys = Object.keys(dataList || {});
		if (keys.length === 0) {
			return <Typography>{t("aux.no_data")}</Typography>;
		}

		return (
			<List>
				{keys.map((key) => {
					const simulation = dataList[key];
					const { task_id, name, action_datetime, simulacion_id, cobertura, holgura } = simulation;

					return (
						<ListItem
							key={key}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								width: "100%",
								height: "8vh",
								border: 1,
								borderColor: "secondary.main",
								borderRadius: 1,
								backgroundColor: "background.paper",
								mb: 1,
							}}
						>
							{/* IZQUIERDA: ojo + métricas */}
							<Box
								sx={{
									width: "260px",
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-start",
									flexShrink: 0,
								}}
							>
								<Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
									<Tooltip title={t("review.tooltips.details")}>
										<IconButton color="primary" onClick={() => handleVisibility(key, dataList)}>
											<VisibilityIcon />
										</IconButton>
									</Tooltip>
								</Box>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 2,
										whiteSpace: "nowrap",
									}}
								>
									{cobertura !== undefined && cobertura !== null && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Typography sx={{ color: "text.primary", fontWeight: 600 }}>
												{t("aux.coverage_short")}
											</Typography>
											<Typography sx={{ color: "success.main", fontWeight: 600 }}>
												{Number(cobertura).toFixed(2)}%
											</Typography>
										</Box>
									)}

									{holgura !== undefined && holgura !== null && (
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Typography sx={{ color: "text.primary", fontWeight: 600 }}>
												{t("aux.slack_short")}
											</Typography>
											<Typography sx={{ color: "error.main", fontWeight: 600 }}>
												{Number(holgura).toFixed(2)}%
											</Typography>
										</Box>
									)}
								</Box>
							</Box>

							{/* CENTRO: nombre + subtítulo */}
							<Box
								sx={{
									flex: 1,
									display: "flex",
									justifyContent: "center",
									minWidth: 0,
									px: 2,
								}}
							>
								<ListItemText
									primary={`${simulacion_id} | ${name}`}
									secondary={`${t("aux.made")}: ${action_datetime}`}
									sx={{
										textAlign: "center",
										m: 0,
										"& .MuiListItemText-primary": {
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										},
										"& .MuiListItemText-secondary": {
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										},
									}}
								/>
							</Box>

							{/* DERECHA: acciones */}
							{statusId === 1 && (
								<Box
									sx={{
										width: "110px",
										display: "flex",
										justifyContent: "flex-end",
										gap: 1,
										flexShrink: 0,
									}}
								>
									<Tooltip title={t("review.tooltips.approve")}>
										<span>
											<IconButton color="success" onClick={() => handleApprove(simulation)}>
												<CheckIcon />
											</IconButton>
										</span>
									</Tooltip>
									<Tooltip title={t("review.tooltips.reject")}>
										<span>
											<IconButton color="error" onClick={() => handleUpdateStatus(task_id, 3)}>
												<CloseIcon />
											</IconButton>
										</span>
									</Tooltip>
								</Box>
							)}

							{statusId === 2 && (
								<Box
									sx={{
										width: "110px",
										display: "flex",
										justifyContent: "flex-end",
										gap: 1,
										flexShrink: 0,
									}}
								>
									<Tooltip title={t("review.tooltips.reject")}>
										<span>
											<IconButton color="error" onClick={() => handleUpdateStatus(task_id, 3)}>
												<CloseIcon />
											</IconButton>
										</span>
									</Tooltip>
								</Box>
							)}

							{statusId === 3 && (
								<Box
									sx={{
										width: "110px",
										display: "flex",
										justifyContent: "flex-end",
										gap: 1,
										flexShrink: 0,
									}}
								>
									<Tooltip title={t("review.tooltips.returnToReview")}>
										<span>
											<IconButton color="primary" onClick={() => handleUpdateStatus(task_id, 1)}>
												<KeyboardReturnIcon />
											</IconButton>
										</span>
									</Tooltip>
								</Box>
							)}
						</ListItem>
					);
				})}
			</List>
		);
	};

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<Typography variant="h5" align="center" sx={{ mb: 2 }}>
				{t("review.title")}
			</Typography>

			{/* Renderizado condicional: si loading, muestra spinner; si no, contenido */}
			{loading ? (
				<Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
					<CircularProgress />
				</Box>
			) : (
				<Box sx={{ width: "75%" }}>
					<Tabs
						value={tabValue}
						onChange={handleChangeTab}
						centered
						variant="fullWidth"
						aria-label="full width tabs example"
					>
						<Tab label={t("review.tabs.review")} />
						<Tab label={t("review.tabs.approved")} />
						<Tab label={t("review.tabs.rejected")} />
					</Tabs>

					<Box
						sx={{
							width: "100%",
							height: "auto",
							border: 1,
							borderColor: "secondary.main",
							borderRadius: 1,
							overflowY: "auto",
							mt: 2,
						}}
					>
						<TabPanel value={tabValue} index={0}>
							{/* En Revisión => status_id=1 */}
							{renderList(dataRevision, 1)}
						</TabPanel>

						<TabPanel value={tabValue} index={1}>
							{/* Aprobadas => status_id=2 */}
							{renderList(dataAprobadas, 2)}
						</TabPanel>

						<TabPanel value={tabValue} index={2}>
							{/* Rechazadas => status_id=3 */}
							{renderList(dataRechazadas, 3)}
						</TabPanel>
					</Box>
				</Box>
			)}

			{/* Modal de detalles (ojito) */}
			<Modal
				open={openModal}
				onClose={handleCloseModals}
				sx={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Card
					sx={{
						width: "70%",
						height: "70%",
						overflowY: "auto",
					}}
				>
					<CardContent>
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								flexDirection: "column",
								width: "100%",
								px: 3,
								py: 2,
							}}
						>
							<Box sx={{ flexGrow: 1 }}>
								<Grid container spacing={2} alignItems="center">
									<Grid item xs={9} sm={9}>
										<Typography variant="h6" gutterBottom>
											{t("sim.pop_up.title")} {selectedItemName ? `"${selectedItemName}"` : t("aux.no_data")}
										</Typography>
									</Grid>
								</Grid>

								<Typography variant="body2" gutterBottom>
									{t("sim.pop_up.desc_sim")}
								</Typography>
								<ul>
									<li>{selectedItemDescription ? selectedItemDescription : t("aux.no_data")}</li>
								</ul>

								<Typography variant="body2" gutterBottom>
									{t("sim.pop_up.conf_site")}
								</Typography>
								<ul>
									{selectedItemTienda && Object.keys(selectedItemTienda).length > 0 ? (
										Object.entries(selectedItemTienda).map(([key, value]) =>
											![
												"id",
												"site_id",
												"task_id",
												"mes_not_cambio",
												"largo_periodo",
												"maximo_turnos",
												"periodos_abierto",
												"horas_no_funcionamiento_tienda",
											].includes(key) ? (
												<li key={key}>
													{key}: {JSON.stringify(value)}
												</li>
											) : null,
										)
									) : (
										<p>{t("sim.pop_up.no_sites")}</p>
									)}
								</ul>

								<Typography variant="body2" gutterBottom>
									{t("sim.pop_up.conf_contract")}
								</Typography>

								<Grid container spacing={2}>
									{(selectedItemContrato || []).length > 0 ? (
										(selectedItemContrato || []).map((contract, index) => {
											// Aseguramos que estos arrays no sean undefined
											const contractPerDay = contract.contracts_per_day || [];
											const contractPerPeriods = contract.contracts_per_periods || [];

											// Combinar
											const combinedData = combineContractsData(contractPerDay, contractPerPeriods, t);

											return (
												<Grid item xs={12} sm={7} md={5} lg={4} key={index}>
													<ul>
														{Object.entries(contract).map(([subKey, subValue]) => {
															// Omitimos ciertas keys
															if (
																[
																	"contracts_per_day",
																	"contracts_per_periods",
																	"id",
																	"config_site_id",
																	"task_id",
																	"contratos",
																].includes(subKey)
															)
																return null;

															return (
																<li key={subKey}>
																	{subKey}: {typeof subValue === "boolean" ? subValue.toString() : subValue}
																</li>
															);
														})}
														<li>
															<strong>{t("aux.period")}:</strong>
															<ul>
																{combinedData.map(({ day, periods }, idx) => (
																	<li key={idx}>
																		{`${day}:`}
																		<ul>
																			{periods.length > 0 ? (
																				periods.map(({ start_time, end_time }, idx2) => (
																					<li key={idx2}>{`${start_time} - ${end_time}`}</li>
																				))
																			) : (
																				<li>{t("aux.no_period")}</li>
																			)}
																		</ul>
																	</li>
																))}
															</ul>
														</li>
													</ul>
												</Grid>
											);
										})
									) : (
										<p>{t("aux.no_data")}</p>
									)}
								</Grid>
							</Box>
						</Box>
					</CardContent>
				</Card>
			</Modal>
		</Box>
	);
};

export { ModuleReview };
